import React, { useState, useRef, useCallback, useEffect } from 'react';
import GameLayout from '../Shared/components/GameLayout/GameLayout';
import Scoreboard from '../Shared/components/Scoreboard/Scoreboard';
import { useAudio } from '../Shared/hooks/useAudio';
import styles from './ClickSpeed.module.css';
import Target from '../Shared/components/Targets/Target';
import { getZoneScore } from '../Shared/utils/TargetUtils';
import Countdown from '../Shared/components/Countdown/Countdown';
import { GAME_THEME } from '../Shared/constants/GameTheme';

const ClickSpeed = () => {
    const { beep, ping } = useAudio();
    const [shots, setShots] = useState([]);
    const [score, setScore] = useState(0);
    const [running, setRunning] = useState(false);
    const [prepMode, setPrepMode] = useState('off');
    const [waiting, setWaiting] = useState(false);
    const [isCountingDown, setIsCountingDown] = useState(false);
    const [showStartModal, setShowStartModal] = useState(true);
    const [currentTime, setCurrentTime] = useState(0);
    const [leaderboard, setLeaderboard] = useState([]);
    const [player, setPlayer] = useState('Player 1');
    const [fillColor, setFillColor] = useState("#cda46a");
    const [confetti, setConfetti] = useState([]);
    const [finishedRank, setFinishedRank] = useState(null);
    const [finishedScore, setFinishedScore] = useState(0);

    const startTimeRef = useRef(null);
    const timerRef = useRef(null);
    const MAX_SHOTS = 6;
    const STORAGE_KEY = "click_speed_runs";

    useEffect(() => {
        const runs = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
        setLeaderboard(runs);
    }, []);

    const actualStart = useCallback(() => {
        beep(1000, 0.2);
        setFillColor(GAME_THEME.TARGET_FILL);
        setWaiting(false);
        setRunning(true);
    }, [beep]);

    const startSequence = useCallback(() => {
        setRunning(false);
        setWaiting(true);
        setShots([]);
        setScore(0);
        setCurrentTime(0);
        startTimeRef.current = null;
        if (timerRef.current) clearInterval(timerRef.current);
        setFinishedRank(null);
        setFinishedScore(0);
        setShowStartModal(false);
        setIsCountingDown(true);
        setFillColor("#8b2a2a");
    }, []);

    const zoneScore = (x, y) => getZoneScore(x, y);

    const saveRun = (time, totalScore, hitCount, missCount) => {
        const runs = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
        const performance = totalScore / time;
        setFinishedScore(performance);

        runs.push({
            player, time, score: totalScore, hits: hitCount, misses: missCount, performance,
            date: new Date().toISOString()
        });
        runs.sort((a, b) => b.performance - a.performance);
        const rank = runs.findIndex(r => r.time === time && r.score === totalScore) + 1;
        setFinishedRank(rank);
        const top = runs.slice(0, 10);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(top));
        setLeaderboard(top);
    };

    const handleTargetClick = (e) => {
        if (!running || waiting || shots.length >= MAX_SHOTS) return;

        if (!startTimeRef.current) {
            startTimeRef.current = performance.now();
            timerRef.current = setInterval(() => {
                setCurrentTime((performance.now() - startTimeRef.current) / 1000);
            }, 10);
        }

        const svg = e.currentTarget;
        const r = svg.getBoundingClientRect();
        const scaleX = 300 / r.width;
        const scaleY = 500 / r.height;
        const x = (e.clientX - r.left) * scaleX;
        const y = (e.clientY - r.top) * scaleY;

        const newShots = [...shots, { x, y }];
        setShots(newShots);

        const pts = zoneScore(x, y);
        setScore(prev => prev + pts);
        if (pts > 0) ping();

        if (newShots.length === MAX_SHOTS) {
            setRunning(false);
            if (timerRef.current) clearInterval(timerRef.current);
            const finalTime = (performance.now() - startTimeRef.current) / 1000;
            setCurrentTime(finalTime);

            let hits = 0;
            let misses = 0;
            newShots.forEach(s => {
                if (getZoneScore(s.x, s.y) > 0) hits++;
                else misses++;
            });

            saveRun(finalTime, score + pts, hits, misses);
            launchConfetti();
        }
    };

    const launchConfetti = () => {
        const pieces = [];
        for (let i = 0; i < 50; i++) {
            pieces.push({
                id: i,
                left: Math.random() * 100 + "vw",
                top: -20 - Math.random() * 200 + "px",
                bg: `hsl(${Math.random() * 360},100%,50%)`
            });
        }
        setConfetti(pieces);
        setTimeout(() => setConfetti([]), 2500);
    };

    const sidebarContent = (
        <>
            <h3 style={{ marginTop: 0 }}>Click Speed</h3>
            <label>Player</label>
            <select value={player} onChange={e => setPlayer(e.target.value)}>
                <option>Player 1</option>
                <option>Player 2</option>
            </select>

            <button
                onClick={startSequence}
                style={{
                    background: GAME_THEME.BUTTON_BG,
                    color: 'white',
                    border: '1px solid #444',
                    padding: '10px',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    width: '100%',
                    marginBottom: '15px'
                }}
            >
                Start Signal
            </button>

            <Scoreboard
                stats={{
                    "Time": currentTime.toFixed(3) + "s",
                    "Points": score,
                    "Clicks": `${shots.length} / ${MAX_SHOTS}`
                }}
                onReset={() => {
                    localStorage.removeItem('clickSpeedLeaderboard');
                    setLeaderboard([]);
                }}
                highScores={leaderboard}
                title="Top 5 Runs"
            />
            <label style={{ display: 'block', marginTop: '20px', fontSize: '12px', color: '#aaa' }}>Prep Mode</label>
            <select
                value={prepMode}
                onChange={(e) => setPrepMode(e.target.value)}
                style={{
                    width: '100%', padding: '8px', background: '#333', color: 'white', border: '1px solid #444', marginTop: '5px'
                }}
            >
                <option value="off">Off</option>
                <option value="on">On (Delay)</option>
            </select>
        </>
    );

    const handleCountdownFinish = useCallback(() => {
        setIsCountingDown(false);
        actualStart();
    }, [actualStart]);

    return (
        <GameLayout
            sidebarContent={sidebarContent}
            gameRunning={running || isCountingDown}
            onQuit={() => {
                setRunning(false);
                setWaiting(false);
                if (timerRef.current) clearInterval(timerRef.current);
                setShots([]);
                setScore(0);
                setCurrentTime(0);
                startTimeRef.current = null;
                setIsCountingDown(false);
                setShowStartModal(true);
            }}
        >
            {showStartModal && (
                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, width: '100%', height: '100%',
                    background: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10
                }}>
                    <div style={{
                        background: '#222', padding: '50px', borderRadius: '16px', textAlign: 'center',
                        border: '1px solid #444', color: '#fff', boxShadow: '0 15px 35px rgba(0,0,0,0.6)', minWidth: '350px'
                    }}>
                        <h1 style={{ marginTop: 0, color: '#f1c40f', fontSize: '2.5em', marginBottom: '10px' }}>Click Speed</h1>
                        <p style={{ color: '#ccc', marginBottom: '30px', fontSize: '1.1em' }}>
                            Click the target zones 6 times as fast as possible.<br />
                            Score is based on efficiency (Points / Time).
                        </p>
                        <button
                            onClick={startSequence}
                            style={{
                                padding: '16px 40px', fontSize: '20px', fontWeight: 'bold',
                                background: GAME_THEME.BUTTON_BG, color: 'white', border: '1px solid #444', borderRadius: '8px', cursor: 'pointer',
                                transition: 'all 0.2s', margin: '0 auto'
                            }}
                        >
                            START GAME
                        </button>
                    </div>
                </div>
            )}
            {isCountingDown && (
                <Countdown prepMode={prepMode === 'on'} onFinish={handleCountdownFinish} />
            )}
            <svg
                viewBox="0 0 300 500"
                className={styles.targetSvg}
                onClick={handleTargetClick}
                style={{ maxHeight: '90vh', backgroundColor: GAME_THEME.BACKGROUND_COLOR }}
            >
                <Target fill={fillColor} x={0} y={0} scale={1} />

                {shots.map((s, i) => (
                    <circle key={i} cx={s.x} cy={s.y} r="5" className={styles.shot} />
                ))}
            </svg>

            {confetti.length > 0 && (
                <div className={styles.confettiContainer}>
                    {confetti.map(c => (
                        <div
                            key={c.id}
                            className={styles.confettiPiece}
                            style={{ left: c.left, top: c.top, background: c.bg }}
                        />
                    ))}
                </div>
            )}
            {finishedRank !== null && (
                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, width: '100%', height: '100%',
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10
                }}>
                    <div style={{
                        background: '#222', padding: '40px', borderRadius: '12px', textAlign: 'center',
                        border: '1px solid #444', color: '#fff', boxShadow: '0 10px 25px rgba(0,0,0,0.5)', minWidth: '300px'
                    }}>
                        <h2 style={{ marginTop: 0, color: '#f1c40f', fontSize: '2em' }}>Game Finished!</h2>

                        <div style={{ margin: '20px 0', fontSize: '1.2em' }}>
                            <div style={{ marginBottom: '10px' }}>
                                <span style={{ fontSize: '2em', fontWeight: 'bold' }}>{finishedScore.toFixed(4)}</span>
                                <span style={{ color: '#aaa', marginLeft: '10px' }}>SCORE</span>
                            </div>
                            <div style={{ color: '#ccc', fontSize: '0.9em' }}>
                                Time: {currentTime.toFixed(2)}s | Points: {score}
                            </div>
                        </div>

                        <div style={{
                            background: '#333', padding: '10px', borderRadius: '5px', marginBottom: '25px', border: '1px solid #555'
                        }}>
                            You placed <strong style={{ color: '#2ecc71', fontSize: '1.2em' }}>#{finishedRank}</strong> on the leaderboard!
                        </div>

                        <button
                            onClick={startSequence}
                            style={{
                                padding: '12px 30px', fontSize: '18px', fontWeight: 'bold',
                                background: GAME_THEME.BUTTON_BG, color: 'white', border: '1px solid #444', borderRadius: '6px',
                                cursor: 'pointer', transition: 'background 0.2s', margin: '0 auto'
                            }}
                        >
                            Play Again
                        </button>
                    </div>
                </div>
            )}
        </GameLayout>
    );
};

export default ClickSpeed;
